import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, track, waLink } from "../lib/api";
import { Section, Reveal, Overline, Button, ease } from "../components/primitives";
import Seo from "../components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => { setP(r.data); track("product_views", { slug }); }).catch(() => setP(false));
  }, [slug]);

  if (p === false) return <Section className="py-40 text-center"><h1 className="display text-5xl text-botanical">Product not found</h1><div className="mt-8 flex justify-center"><Button to="/shop">Back to Shop</Button></div></Section>;
  if (!p) return <div className="py-40 text-center text-muted-foreground">Loading…</div>;

  return (
    <div>
      <Seo title={p.seo_title || p.name} description={p.seo_description || p.short_description} />
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-20 pt-12 pb-20" style={{ background: `${p.color}0a` }}>
        <div className="mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease }}>
            <img src={p.image} alt={p.name} className="w-full max-w-md mx-auto object-contain drop-shadow-2xl" />
          </motion.div>
          <div>
            <Overline>{p.positioning}</Overline>
            <h1 className="display text-6xl md:text-7xl mt-4" style={{ color: p.color }} data-testid="product-title">{p.name}</h1>
            <p className="font-serif text-2xl italic text-botanical/70 mt-3">{p.tagline}</p>
            <p className="text-muted-foreground font-light text-lg mt-6 leading-relaxed">{p.description}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {(p.verified_claims || []).map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 text-xs border border-border rounded-full px-3 py-1.5 text-botanical/80"><Check className="h-3.5 w-3.5" style={{ color: p.color }} />{c}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <Button href={waLink(`Hi Cultiva Botanics, I'm interested in ${p.name}.`)} onClick={() => track("whatsapp_click", { slug })} testId="product-enquire-btn">Enquire on WhatsApp</Button>
              <Button to="/wholesale" variant="outline">Wholesale Enquiry</Button>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-14">
          <Reveal>
            <Overline>The Ingredient</Overline>
            <h2 className="display text-4xl text-botanical mt-3">Ingredient Story</h2>
            <p className="text-muted-foreground font-light text-lg mt-4 leading-relaxed">{p.ingredient_story}</p>
          </Reveal>
          <Reveal>
            <Overline>How to Use</Overline>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              {(p.usage || []).map((u, i) => (
                <div key={i} className="flex items-start gap-3 border border-border rounded-md p-4 bg-white">
                  <span className="font-serif text-2xl" style={{ color: p.color }}>0{i + 1}</span>
                  <span className="text-sm text-botanical/80 pt-1">{u}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <Overline>Frequently Asked</Overline>
            <Accordion type="single" collapsible className="mt-4">
              {(p.faqs || []).map((faq, i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left font-serif text-xl text-botanical">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
        <aside className="space-y-6">
          <div className="border border-border rounded-md p-6 bg-white">
            <Overline>Details</Overline>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Ingredients</dt><dd className="text-botanical text-right max-w-[60%]">{p.ingredients}</dd></div>
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Available Sizes</dt><dd className="text-botanical">{(p.pack_sizes || []).join(", ")}</dd></div>
              <div className="flex justify-between border-b border-border pb-2"><dt className="text-muted-foreground">Category</dt><dd className="text-botanical">{p.category}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Nutrition</dt><dd className="text-botanical text-right max-w-[60%]">{p.nutrition}</dd></div>
            </dl>
          </div>
          <div className="border border-border rounded-md p-6" style={{ background: `${p.color}0d` }}>
            <Overline>Quality & Documentation</Overline>
            <p className="text-sm text-botanical/70 mt-3">Product documentation is available upon qualified business enquiry.</p>
            <div className="mt-4"><Button to="/quality" variant="ghost">View Quality</Button></div>
          </div>
        </aside>
      </Section>

      {/* B2B CTA */}
      <Section className="py-24 bg-botanical text-ivory rounded-none grain">
        <div className="relative z-10 max-w-3xl">
          <Overline className="text-ivory/50">For Business</Overline>
          <h2 className="display text-5xl md:text-6xl mt-4">Buying for Your Business?</h2>
          <p className="text-ivory/70 font-light text-lg mt-5">We welcome enquiries from distributors, importers, wholesalers, retailers and international buyers.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button to="/wholesale" variant="light" testId="product-wholesale-btn">Wholesale Enquiry</Button>
            <Button to="/become-a-distributor" variant="outline" className="border-ivory/30 text-ivory hover:bg-ivory hover:text-botanical" testId="product-distributor-btn">Become a Distributor</Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
